/**
 * SubNav — frosted-parchment strip naming the active dataset, pair count, and
 * the source's validity date. Grounded facts only: dataset · pairs · "valid
 * until <date>". No outdated/expiry warning or ⚠ — the date IS the message; the
 * nurse reads it and decides. (No paternalistic hedges in clinical UI.)
 */

import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import type { CompatibilityBoardState } from '@frontend/components/ysite/types'

interface SubNavProps {
  state: CompatibilityBoardState
  pairCount: number
}

export function SubNav({ state, pairCount }: SubNavProps) {
  const { t } = useTranslation()
  const dataset = state.source_doc.replace('/', ' ')

  return (
    <div
      className={cn(
        'flex-shrink-0 border-b border-[var(--divider)]',
        'bg-[color-mix(in_srgb,var(--parchment)_85%,transparent)]',
        'supports-[backdrop-filter]:backdrop-blur-xl supports-[backdrop-filter]:backdrop-saturate-150',
      )}
    >
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-2.5 sm:px-6">
        <h2 className="font-display text-[17px] font-semibold tracking-[-0.3px] text-neutral-900">
          {t('compatibility.subtitle')}
        </h2>

        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px] text-neutral-500">
          <span>
            {t('compatibility.datasetLabel')}{' '}
            <b className="font-semibold text-neutral-900">{dataset}</b>
          </span>
          <Dot />
          <span>{t('compatibility.pairsCount', { count: pairCount })}</span>
          <Dot />
          <span>
            {t('compatibility.validUntil')}{' '}
            <b className="font-semibold text-neutral-900">{state.valid_until}</b>
          </span>
        </div>
      </div>
    </div>
  )
}

function Dot() {
  return (
    <span
      aria-hidden
      className="hidden h-1 w-1 rounded-full bg-neutral-400 sm:inline-block"
    />
  )
}
